import jsPDF from 'jspdf';
import { SERENO_LOGO_PNG, SERENO_LOGO_RATIO } from './serenoLogo';

// ─── Constants ───────────────────────────────────────────────────────────────

const SERENO = {
  name: 'Sereno Tech S.L.',
  cif: 'B44916658',
  address: 'Calle Tulipán, 1',
  city: 'Barbate',
  cp: '11160',
  province: 'Cádiz',
};

type RGB = [number, number, number];

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

// 'always' keeps the thousands separator on 4-digit amounts (es-ES drops it by default)
const NUM_FMT = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
} as any);

function fmtN(n: number): string {
  return NUM_FMT.format(n);
}

function fmt(n: number): string {
  return fmtN(n) + ' €';
}

/** Signed amount, e.g. "-168,69 €" */
function fmtSigned(n: number): string {
  return (n < 0 ? '-' : '') + fmtN(Math.abs(n)) + ' €';
}

/** "2026-06-05T14:00:00" | "2026-06-05" → "05.06.2026". Other formats pass through. */
function fmtDate(s: any): string {
  const str = String(s || '');
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : str;
}

/** IBAN in groups of 4 for readability */
function fmtIban(iban: string): string {
  return iban.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Truncates with an ellipsis so the text fits maxW (mm) at the current font */
function clip(doc: jsPDF, text: string, maxW: number): string {
  if (doc.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 1 && doc.getTextWidth(t + '...') > maxW) t = t.slice(0, -1);
  return t + '...';
}

function lastDayOfMonth(monthName: string, year: string): number {
  const mIdx = MONTHS.indexOf(monthName);
  if (mIdx === -1) return 30;
  return new Date(parseInt(year), mIdx + 1, 0).getDate();
}

// ─── Owner adapter ────────────────────────────────────────────────────────────
// Converts CRM Owner record into the flat format used by the PDF functions.

/** Segundo titular en propiedades compartidas (facturación a dos propietarios) */
interface PdfSecondOwner {
  name: string;
  cif: string;
  address: string;
  /** % de las ganancias que le corresponde (el titular 1 se lleva el resto) */
  percent: number;
}

interface PdfOwner {
  name: string;
  cif: string;
  address: string;
  city: string;
  cp: string;
  province: string;
  country: string;
  phone: string;
  email: string;
  iban: string;
  facturaPrefix: string;
  facturaNum: number | null;
  comision: number;
  modelo: string;
  /** null salvo que el propietario tenga activada la facturación a dos titulares */
  second: PdfSecondOwner | null;
}

/**
 * Reparte un importe entre los dos titulares sin perder céntimos por redondeo:
 * el titular 1 se queda con su porcentaje redondeado y el titular 2 con el resto,
 * de modo que las dos partes siempre suman el total exacto.
 */
function splitAmount(total: number, secondPercent: number): [number, number] {
  const first = round2(total * ((100 - secondPercent) / 100));
  return [first, round2(total - first)];
}

/** "50" en vez de "50,00" cuando el porcentaje es entero */
function fmtPct(n: number): string {
  return Number.isInteger(n) ? String(n) : fmtN(n);
}

function secondOwnerToPdf(owner: any): PdfSecondOwner | null {
  if (!owner.secondOwnerEnabled) return null;
  const name = String(owner.secondOwnerName || '').trim();
  const cif = String(owner.secondOwnerDni || '').trim();
  const address = String(owner.secondOwnerBillingAddress || '').trim();
  if (!name && !cif && !address) return null;
  const pct = Number(owner.secondOwnerPercent);
  return {
    name,
    cif,
    address,
    percent: Number.isFinite(pct) && pct >= 0 && pct <= 100 ? pct : 50,
  };
}

export function ownerToPdf(owner: any, modelo: string, liq?: any): PdfOwner {
  const yearSuffix = String(new Date().getFullYear()).slice(-2);
  // Use invoice number snapshotted at liquidación creation time if available
  const facturaPrefix = liq?.invoicePrefix ?? owner.invoicePrefix ?? `SERENO${yearSuffix}/`;
  const facturaNum = liq?.invoiceNum ?? owner.invoiceNextNum ?? null;
  return {
    name: `${owner.firstName || ''} ${owner.lastName || ''}`.trim(),
    cif: owner.dni || '',
    address: owner.address?.street || '',
    city: owner.address?.city || '',
    cp: owner.address?.postalCode || '',
    province: owner.address?.province || '',
    country: owner.address?.country || '',
    phone: owner.phone || '',
    email: owner.email || '',
    iban: owner.iban || '',
    facturaPrefix,
    facturaNum,
    comision: owner.commissionPercent ?? 20,
    modelo,
    second: secondOwnerToPdf(owner),
  };
}

// ─── PDF: Liquidación (Gestión / Rent2Rent) ──────────────────────────────────
//
// Mismo lenguaje que la factura que Sereno emite desde Holded, porque al
// propietario le llegan juntas: blanco y negro, líneas finas en vez de barras
// de color, bloques de emisor y propietario, totales a la derecha entre líneas
// y pie con el número de página.

/** Datos de Sereno tal y como salen en sus facturas de Holded */
const SERENO_EMISOR = {
  name: 'SERENO TECH S.L.',
  lines: ['Calle Tulipán, 1', 'Barbate (11160), Cádiz, España', 'B44916658', '623008140', 'gestion@tusereno.es'],
};

type CategoriaLiq = 'ingresos' | 'plataforma' | 'gestion' | 'renting' | 'gasto';

const SECCIONES_LIQ: Record<CategoriaLiq, string> = {
  ingresos: 'Ingresos',
  plataforma: 'Comisiones de plataforma',
  gestion: 'Gestión Sereno',
  renting: 'Servicios adicionales',
  gasto: 'Gastos adicionales',
};
const ORDEN_LIQ: CategoriaLiq[] = ['ingresos', 'plataforma', 'gestion', 'renting', 'gasto'];

/** Las liquidaciones antiguas no guardan la categoría: se deduce del texto, como en la pantalla */
function categoriaDe(c: any): CategoriaLiq {
  if (c.categoria && c.categoria in SECCIONES_LIQ) return c.categoria;
  const l = String(c.label || '').toLowerCase();
  if (l.startsWith('gasto:') || l.startsWith('[')) return 'gasto';
  if (l.includes('plataforma') || l.includes('canal') || l.includes('transacción') || l.includes('ota')) return 'plataforma';
  if (l.includes('gestión') || l.includes('gestion') || l.includes('directa')) return 'gestion';
  if (l.includes('renting')) return 'renting';
  return 'ingresos';
}

/** Importe al estilo de Holded: "1.897,51€" */
function fmtH(n: number): string {
  return fmtN(n) + '€';
}

function _buildLiquidacionDoc(doc: jsPDF, owner: PdfOwner, liq: any, reservas: any[], periodo: string): jsPDF {
  const W = 210, M = 12, RX = W - M;
  const TOP = 18, BOTTOM = 272;
  const ink: RGB = [17, 17, 17];
  const muted: RGB = [120, 120, 120];
  const rule: RGB = [17, 17, 17];
  const faint: RGB = [210, 210, 210];
  let y = 0;

  const ensure = (h: number, alNuevaPagina?: () => void) => {
    if (y + h <= BOTTOM) return;
    doc.addPage();
    y = TOP;
    alNuevaPagina?.();
  };
  const hr = (x1: number, x2: number, color: RGB = rule, grosor = 0.3) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(grosor);
    doc.line(x1, y, x2, y);
  };
  const texto = (t: string, x: number, yy: number, o: { size?: number; bold?: boolean; color?: RGB; align?: 'left' | 'right' } = {}) => {
    doc.setFont('helvetica', o.bold ? 'bold' : 'normal');
    doc.setFontSize(o.size ?? 8);
    doc.setTextColor(...(o.color ?? ink));
    doc.text(t, x, yy, o.align === 'right' ? { align: 'right' } : undefined);
  };

  // ── Cabecera: título, logo y datos del documento ───────────────────────────
  texto('Liquidación', M, 20, { size: 22, bold: true });
  const logoW = 34;
  try {
    // Comprimido: sin 'FAST' jsPDF mete el PNG sin comprimir y el PDF pasa de ~28 KB a ~460 KB
    doc.addImage(SERENO_LOGO_PNG, 'PNG', RX - logoW, 9, logoW, logoW * SERENO_LOGO_RATIO, 'logo-sereno', 'FAST');
  } catch { /* sin logo, el documento sigue valiendo */ }

  const [mesNombre, anio] = periodo.split(' ');
  const mIdx = MONTHS.indexOf(mesNombre);
  const fecha = mIdx >= 0 && anio
    ? `${String(lastDayOfMonth(mesNombre, anio)).padStart(2, '0')}/${String(mIdx + 1).padStart(2, '0')}/${anio}`
    : '';
  const datos: [string, string][] = [['Periodo', periodo], ...(fecha ? [['Fecha', fecha] as [string, string]] : [])];
  datos.forEach(([k, v], i) => {
    texto(k, M + 1, 30 + i * 5.2, { size: 8, bold: true });
    texto(v, M + 50, 30 + i * 5.2, { size: 8, align: 'right' });
  });

  // ── Emisor (Sereno) y propietario ──────────────────────────────────────────
  y = 30 + datos.length * 5.2 + 9;
  const colProp = 139;
  const LH = 4.4;
  texto(SERENO_EMISOR.name, M, y, { size: 7.8, bold: true });
  SERENO_EMISOR.lines.forEach((l, i) => texto(l, M, y + (i + 1) * LH, { size: 7.8 }));

  const ciudad = [owner.city && owner.cp ? `${owner.city} (${owner.cp})` : owner.city || owner.cp, owner.province]
    .filter(Boolean).join(', ');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  const propLines = [owner.name, owner.address, ciudad, owner.cif].filter(Boolean).map(String)
    .flatMap(l => doc.splitTextToSize(l, RX - colProp) as string[]);
  texto(owner.second ? 'Titular 1' : 'Propietario', colProp, y, { size: 7.8, bold: true });
  propLines.forEach((l, i) => texto(l, colProp, y + (i + 1) * LH, { size: 7.8 }));
  y += (Math.max(SERENO_EMISOR.lines.length, propLines.length) + 1) * LH + 10;

  // ── Conceptos ──────────────────────────────────────────────────────────────
  const X_ING = 158;
  const cabeceraConceptos = () => {
    texto('CONCEPTO', M, y, { size: 6 });
    texto('INGRESOS', X_ING, y, { size: 6, align: 'right' });
    texto('PAGOS', RX, y, { size: 6, align: 'right' });
    y += 2.4;
    hr(M, RX);
    y += 6.5;
  };
  cabeceraConceptos();

  const conceptos: any[] = liq.conceptos || [];
  const grupos = new Map<CategoriaLiq, any[]>();
  for (const c of conceptos) {
    const cat = categoriaDe(c);
    if (!grupos.has(cat)) grupos.set(cat, []);
    grupos.get(cat)!.push(c);
  }

  for (const cat of ORDEN_LIQ) {
    const items = grupos.get(cat);
    if (!items?.length) continue;
    ensure(6 + 4.6 * Math.min(items.length, 3), cabeceraConceptos);
    texto(SECCIONES_LIQ[cat], M, y, { size: 8, bold: true });
    y += 5;
    for (const c of items) {
      doc.setFontSize(7.8);
      const lines = doc.splitTextToSize(String(c.label || ''), X_ING - M - 30) as string[];
      ensure(lines.length * 3.8 + 1, cabeceraConceptos);
      lines.forEach((l, li) => texto(l, M, y + li * 3.8, { size: 7.8 }));
      if (c.ing != null && c.ing !== 0) texto(fmtH(c.ing), X_ING, y, { size: 7.8, align: 'right' });
      if (c.pago != null && c.pago !== 0) texto(fmtH(c.pago), RX, y, { size: 7.8, align: 'right' });
      y += Math.max(1, lines.length) * 3.8 + 0.9;
    }
    y += 4;
  }

  // ── Totales, a la derecha entre líneas ─────────────────────────────────────
  const totalIngresos = conceptos.reduce((s, c) => s + (c.ing || 0), 0);
  const totalPagos = conceptos.reduce((s, c) => s + Math.abs(c.pago || 0), 0);
  const XT = 105;
  ensure(34);
  y += 1;
  hr(XT, RX);
  y += 7;
  texto('TOTAL INGRESOS', XT + 0.5, y, { size: 8, bold: true });
  texto(fmtH(totalIngresos), RX, y, { size: 8, bold: true, align: 'right' });
  y += 8.5;
  texto('TOTAL PAGOS', XT + 0.5, y, { size: 8, bold: true });
  texto(fmtH(-totalPagos), RX, y, { size: 8, bold: true, align: 'right' });
  y += 4.5;
  hr(XT, RX);
  y += 7;
  // Negativo: lo debe el propietario a Sereno (un mes con solo su propia estancia, por ejemplo)
  texto(liq.saldo < 0 ? 'SALDO A FAVOR DE SERENO' : 'SALDO A FAVOR DEL PROPIETARIO', XT + 0.5, y, { size: 8.5, bold: true });
  texto(fmtH(Math.abs(liq.saldo)), RX, y, { size: 8.5, bold: true, align: 'right' });
  y += 14;

  // ── Reparto entre titulares (propiedad compartida) ─────────────────────────
  if (owner.second) {
    const pct2 = owner.second.percent;
    const pct1 = round2(100 - pct2);
    const [saldo1, saldo2] = splitAmount(Math.abs(liq.saldo), pct2);

    ensure(40);
    texto('Reparto entre titulares', M, y, { size: 9.5, bold: true });
    y += 7;
    texto('TITULAR', M, y, { size: 6 });
    texto('CONCEPTO', 78, y, { size: 6 });
    texto('IMPORTE', RX, y, { size: 6, align: 'right' });
    y += 2.4;
    hr(M, RX);
    y += 6.5;

    const titular = (rotulo: string, nombre: string, cif: string, pct: number, importe: number) => {
      texto(clip(doc, nombre || rotulo, 58), M, y, { size: 7.8, bold: true });
      texto([rotulo, cif].filter(Boolean).join(' · '), M, y + 4, { size: 7.2 });
      texto(`Rentas liquidadas — ${periodo} (${fmtPct(pct)}%)`, 78, y, { size: 7.8 });
      texto(fmtH(importe), RX, y, { size: 7.8, align: 'right' });
      y += 11;
    };
    titular('Titular 1', owner.name, owner.cif, pct1, saldo1);
    titular('Titular 2', owner.second.name, owner.second.cif, pct2, saldo2);

    if (owner.second.address) {
      const dir = owner.second.address.split('\n').map(s => s.trim()).filter(Boolean).join(', ');
      texto(clip(doc, `Dirección de facturación del titular 2: ${dir}`, RX - M), M, y - 3, { size: 6.8, color: muted });
      y += 3;
    }
    y += 6;
  }

  // ── Detalle de reservas ────────────────────────────────────────────────────
  if (reservas.length > 0) {
    const C = {
      prop: M, ent: 58, n: 80, canal: 83,
      tot: 114, limp: 130, com: 147, ser: 163, rent: 179, ing: RX,
    };
    const cabeceraReservas = () => {
      const h = { size: 5.6 };
      texto('PROPIEDAD', C.prop, y, h);
      texto('ENTRADA', C.ent, y, h);
      texto('HUÉSP.', C.n, y, { ...h, align: 'right' });
      texto('CANAL', C.canal, y, h);
      texto('TOTAL', C.tot, y, { ...h, align: 'right' });
      texto('LIMPIEZA', C.limp, y, { ...h, align: 'right' });
      texto('COM. CANAL', C.com, y, { ...h, align: 'right' });
      texto('SERENO', C.ser, y, { ...h, align: 'right' });
      texto('RENTING', C.rent, y, { ...h, align: 'right' });
      texto('INGRESOS', C.ing, y, { ...h, align: 'right' });
      y += 2.2;
      hr(M, RX);
      y += 4.8;
    };

    ensure(30);
    texto('Detalle de reservas', M, y, { size: 9.5, bold: true });
    y += 4.5;
    texto('Com. canal, Sereno y Renting con IVA (21%) incluido.', M, y, { size: 6.5, color: muted });
    y += 7;
    cabeceraReservas();

    const canalConIvaOf = (r: any) => r.canalConIva ?? ((r.comisionCanal || 0) + (r.tarifaTransaccion || 0));
    const serenoConIvaOf = (r: any) => (r.comisionSereno || 0) + (r.ivaSereno || 0);
    const rentingConIvaOf = (r: any) => r.rentingConIva ?? (r.renting || 0);
    const ingresosOf = (r: any) => r.ingresos != null
      ? r.ingresos
      : (r.precioTotal || 0) - (r.limpieza || 0) - canalConIvaOf(r) - serenoConIvaOf(r) - rentingConIvaOf(r);

    const f = { size: 6.4 };
    reservas.forEach(r => {
      ensure(5, cabeceraReservas);
      texto((r.anuncio || '').substring(0, 24), C.prop, y, f);
      texto(fmtDate(r.entrada), C.ent, y, f);
      texto(r.huespedes != null ? String(r.huespedes) : '—', C.n, y, { ...f, align: 'right' });
      texto((r.canal || '').substring(0, 12), C.canal, y, f);
      texto(fmtH(r.precioTotal || 0), C.tot, y, { ...f, align: 'right' });
      texto(fmtH(r.limpieza || 0), C.limp, y, { ...f, align: 'right' });
      texto(fmtH(canalConIvaOf(r)), C.com, y, { ...f, align: 'right' });
      texto(fmtH(serenoConIvaOf(r)), C.ser, y, { ...f, align: 'right' });
      texto(fmtH(rentingConIvaOf(r)), C.rent, y, { ...f, align: 'right' });
      texto(fmtH(ingresosOf(r)), C.ing, y, { ...f, bold: true, align: 'right' });
      y += 4.6;
    });

    ensure(9, cabeceraReservas);
    y -= 1.6;
    hr(M, RX, faint, 0.2);
    y += 4.2;
    const suma = (fn: (r: any) => number) => reservas.reduce((s, r) => s + fn(r), 0);
    const b = { ...f, bold: true };
    texto('TOTALES', C.prop, y, b);
    texto(fmtH(suma(r => r.precioTotal || 0)), C.tot, y, { ...b, align: 'right' });
    texto(fmtH(suma(r => r.limpieza || 0)), C.limp, y, { ...b, align: 'right' });
    texto(fmtH(suma(canalConIvaOf)), C.com, y, { ...b, align: 'right' });
    texto(fmtH(suma(serenoConIvaOf)), C.ser, y, { ...b, align: 'right' });
    texto(fmtH(suma(rentingConIvaOf)), C.rent, y, { ...b, align: 'right' });
    texto(fmtH(suma(ingresosOf)), C.ing, y, { ...b, align: 'right' });
    y += 8;
  }

  // ── Pie en todas las páginas, como el de Holded ────────────────────────────
  const paginas = doc.getNumberOfPages();
  for (let p = 1; p <= paginas; p++) {
    doc.setPage(p);
    doc.setDrawColor(...faint);
    doc.setLineWidth(0.3);
    doc.line(M, 281, RX, 281);
    texto(`Liquidación ${periodo} - ${owner.name}`, M, 286, { size: 6 });
    texto(`Pág. ${p} de ${paginas}`, RX, 286, { size: 6, align: 'right' });
  }

  return doc;
}

export function generateLiquidacionPDF(owner: PdfOwner, liq: any, reservas: any[], periodo: string): void {
  const doc = _buildLiquidacionDoc(new jsPDF({ format: 'a4', unit: 'mm' }), owner, liq, reservas, periodo);
  doc.save(`Liquidacion_${owner.name.replace(/\s+/g, '_')}_${periodo.replace(' ', '_')}.pdf`);
}

// ─── PDF: Factura (Explotación) ──────────────────────────────────────────────

function _buildFacturaDoc(doc: jsPDF, owner: PdfOwner, liq: any, periodo: string): jsPDF {
  // ── Geometry ───────────────────────────────────────────────────────────────
  const W = 210;         // A4 width
  const M = 15;          // left margin
  const RX = W - M;      // right edge
  const COL2 = 112;      // second column (PARA / summary labels)
  const TX = 128;        // totals block label x
  const LINE = 4.35;     // interlineado de los bloques de datos fiscales
  const BOTTOM = 276;    // content bottom limit

  // ── Palette (matches the reference invoice) ────────────────────────────────
  const navy: RGB = [31, 59, 95];
  const ink: RGB = [28, 39, 51];
  const slate: RGB = [88, 97, 108];
  const muted: RGB = [150, 157, 166];
  const hair: RGB = [226, 231, 237];
  const hairSoft: RGB = [236, 239, 242];

  if (owner.facturaNum == null) {
    throw new Error('El propietario no tiene configurado el número de factura (invoiceNextNum). Edítalo antes de generar la factura.');
  }

  const [month, yearStr] = periodo.split(' ');
  const factNum = `${owner.facturaPrefix}${owner.facturaNum}`;
  const mIdx = MONTHS.indexOf(month);
  const lastDay = lastDayOfMonth(month, yearStr);
  const dateStr = `${String(lastDay).padStart(2, '0')}/${String(mIdx + 1).padStart(2, '0')}/${yearStr}`;

  const isLcm = owner.modelo === 'explotacion_lcm';
  const alquiler = liq.alquiler || 0;
  const iva = liq.iva || 0;
  const irpf = liq.irpf || 0;
  const total = liq.total || 0;
  // Lozoya-style exceptions add the cleaning fee straight to the payout, so the
  // invoice total is more than base + IVA - IRPF. Show it as its own line.
  const extra = round2(total - (alquiler + iva - irpf));

  let y = 0;
  const ensure = (h: number) => {
    if (y + h > BOTTOM) { doc.addPage(); y = 20; return true; }
    return false;
  };
  const rule = (x1: number, yy: number, x2: number, color: RGB = hair, width = 0.2) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(width);
    doc.line(x1, yy, x2, yy);
  };

  // ── Header: FACTURA + nº / fecha box ───────────────────────────────────────
  const boxY = 13, boxH = 17.2, boxX = RX - 66;
  doc.setDrawColor(...hair);
  doc.setLineWidth(0.3);
  doc.roundedRect(boxX, boxY, RX - boxX, boxH, 2, 2, 'S');
  rule(boxX, boxY + boxH / 2, RX, hair, 0.3);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.75);
  doc.setTextColor(...slate);
  doc.text('Nº FACTURA', boxX + 3.2, boxY + 5.4, { charSpace: 0.3 });
  doc.text('FECHA', boxX + 3.2, boxY + boxH / 2 + 5.4, { charSpace: 0.3 });

  doc.setTextColor(...navy);
  doc.setFontSize(9.75);
  doc.text(factNum, RX - 3.2, boxY + 5.4, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.75);
  doc.text(dateStr, RX - 3.2, boxY + boxH / 2 + 5.4, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22.5);
  doc.setTextColor(...navy);
  doc.text('FACTURA', M, boxY + 9);

  y = boxY + boxH + 7.5;

  // ── Emisor / Para ──────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...navy);
  doc.text(owner.second ? 'TITULAR 1' : 'EMISOR (PROPIETARIO)', M, y, { charSpace: 0.2 });
  doc.text('PARA', COL2, y, { charSpace: 0.2 });
  y += 5;

  const localidad = [owner.city, owner.cp, owner.province, owner.country].filter(Boolean).join(', ');
  const emisorLines = [owner.name, owner.cif, owner.address, localidad, owner.phone, owner.email]
    .filter(Boolean).map(String);
  const paraLines = [SERENO.name, SERENO.cif, SERENO.address, `${SERENO.cp} ${SERENO.city}, ${SERENO.province}`];

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.25);
  doc.setTextColor(...ink);
  // Las direcciones largas se parten dentro de su columna: sin esto pisaban «PARA»
  const emisorWrapped = emisorLines.flatMap(l => doc.splitTextToSize(l, COL2 - M - 6) as string[]);
  emisorWrapped.forEach((l, i) => doc.text(l, M, y + i * LINE));
  paraLines.forEach((l, i) => doc.text(l, COL2, y + i * LINE));
  y += Math.max(emisorWrapped.length, paraLines.length) * LINE + 6;

  // ── Periodo ────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.75);
  doc.setTextColor(...navy);
  doc.text(`Mes: ${periodo}`, M, y);
  y += 7.5;

  // ── Concepto ───────────────────────────────────────────────────────────────
  const conceptoBase = `Renta variable por cesión de explotación — ${periodo}`;

  if (owner.second) {
    // Propiedad compartida: un desglose por titular, cada uno con su parte de
    // base imponible, IVA e IRPF. Las dos partes suman exactamente el total.
    const pct2 = owner.second.percent;
    const pct1 = round2(100 - pct2);
    const [base1, base2] = splitAmount(alquiler, pct2);
    const [iva1, iva2] = splitAmount(iva, pct2);
    const [irpf1, irpf2] = splitAmount(irpf, pct2);
    const showIrpf = irpf > 0;

    const cIrpf = RX;
    const cIva = showIrpf ? 172 : RX;
    const cBase = showIrpf ? 148 : 168;

    const conceptoHeader = () => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.75);
      doc.setTextColor(...muted);
      doc.text('Concepto', M, y);
      doc.text('Base imponible', cBase, y, { align: 'right' });
      doc.text('IVA (21%)', cIva, y, { align: 'right' });
      if (showIrpf) doc.text('IRPF (19%)', cIrpf, y, { align: 'right' });
      y += 2.6;
      rule(M, y, RX, hairSoft, 0.4);
      y += 4.3;
    };

    const conceptoRow = (pct: number, b: number, v: number, r: number) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.125);
      doc.setTextColor(...ink);
      doc.text(clip(doc, `${conceptoBase} (${fmtPct(pct)}%)`, cBase - M - 26), M, y);
      doc.text(fmt(b), cBase, y, { align: 'right' });
      doc.text(fmt(v), cIva, y, { align: 'right' });
      if (showIrpf) doc.text(fmtSigned(-r), cIrpf, y, { align: 'right' });
      y += 3.2;
      rule(M, y, RX, hairSoft, 0.4);
      y += 6.9;
    };

    conceptoHeader();
    conceptoRow(pct1, base1, iva1, irpf1);

    // Titular 2: sus datos fiscales y su propio desglose
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.25);
    const t2Lines = [owner.second.name, owner.second.cif, ...owner.second.address.split('\n')]
      .map(l => String(l || '').trim()).filter(Boolean)
      .flatMap(l => doc.splitTextToSize(l, RX - M) as string[]);
    ensure(t2Lines.length * LINE + 28);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...navy);
    doc.text('TITULAR 2', M, y, { charSpace: 0.2 });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.25);
    doc.setTextColor(...ink);
    t2Lines.forEach((l, i) => doc.text(l, M, y + i * LINE));
    y += t2Lines.length * LINE + 6;

    conceptoHeader();
    conceptoRow(pct2, base2, iva2, irpf2);
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.75);
    doc.setTextColor(...muted);
    doc.text('Concepto', M, y);
    doc.text(isLcm ? 'Importe (base)' : 'Importe', RX, y, { align: 'right' });
    y += 2.6;
    rule(M, y, RX, hairSoft, 0.4);
    y += 4.3;

    doc.setFontSize(7.125);
    doc.setTextColor(...ink);
    doc.text(conceptoBase, M, y);
    doc.text(fmt(alquiler), RX, y, { align: 'right' });
    y += 3.2;
    rule(M, y, RX, hairSoft, 0.4);
    y += 6.9;
  }

  // ── Totales ────────────────────────────────────────────────────────────────
  const totalRows: [string, string][] = [
    [owner.second ? 'Base imponible total' : 'Base imponible', fmt(alquiler)],
    ['IVA 21%', fmt(iva)],
  ];
  if (irpf > 0) totalRows.push(['Retención IRPF 19%', fmtSigned(-irpf)]);
  if (Math.abs(extra) >= 0.01) totalRows.push(['Tarifa de limpieza repercutida', fmtSigned(extra)]);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.25);
  doc.setTextColor(...ink);
  totalRows.forEach((r, i) => {
    doc.text(r[0], TX, y + i * 5.95);
    doc.text(r[1], RX, y + i * 5.95, { align: 'right' });
  });
  y += (totalRows.length - 1) * 5.95 + 3.3;
  rule(TX, y, RX, navy, 0.5);
  y += 5.4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...navy);
  doc.text('TOTAL', TX, y);
  doc.text(fmt(total), RX, y, { align: 'right' });
  y += 4;

  // ── Forma de pago ──────────────────────────────────────────────────────────
  if (owner.iban) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.4);
    doc.setTextColor(...muted);
    doc.text(`Forma de pago: transferencia bancaria · IBAN ${fmtIban(owner.iban)}`, RX, y, { align: 'right' });
    y += 4;
  }
  y += 5.5;

  // ── Desglose de reservas ───────────────────────────────────────────────────
  const reservasDetalle: any[] = liq.reservasDetalle || [];
  // LCM: la limpieza se muestra en base imponible (importe/1,21), como la herramienta de liquidaciones
  const limpiezaRow = (r: any) => (isLcm ? (r.limpiezaBI ?? r.limpieza) : r.limpieza) || 0;
  const comisionRow = (r: any) => (r.comisionCanal || 0) + (r.tarifaTransaccion || 0);

  if (reservasDetalle.length > 0) {
    const C = {
      prop:  M,     // left
      ent:   44,    // left
      sal:   66,    // left
      canal: 88,    // left
      tot:   133,   // right
      limp:  152,   // right
      com:   172,   // right
      rent:  188,   // right
      n:     RX,    // right
    };

    const tableHeader = () => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.75);
      doc.setTextColor(...muted);
      doc.text('Propiedad',  C.prop,  y);
      doc.text('Entrada',    C.ent,   y);
      doc.text('Salida',     C.sal,   y);
      doc.text('Canal',      C.canal, y);
      doc.text('Total',      C.tot,   y, { align: 'right' });
      doc.text('Limpieza',   C.limp,  y, { align: 'right' });
      doc.text('Com.canal',  C.com,   y, { align: 'right' });
      doc.text('Renting',    C.rent,  y, { align: 'right' });
      doc.text('N',          C.n,     y, { align: 'right' });
      y += 2.6;
      rule(M, y, RX, hairSoft, 0.4);
      y += 3.5;
    };

    ensure(26);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.25);
    doc.setTextColor(...navy);
    doc.text('Desglose de reservas', M, y);
    y += 6.1;
    tableHeader();

    reservasDetalle.forEach(r => {
      if (ensure(7)) tableHeader();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.125);
      doc.setTextColor(...ink);
      doc.text(clip(doc, String(r.anuncio || ''), 27), C.prop, y);
      doc.text(fmtDate(r.entrada), C.ent, y);
      doc.text(fmtDate(r.salida), C.sal, y);
      doc.text(clip(doc, String(r.canal || ''), 29), C.canal, y);
      doc.text(fmt(r.precioTotal || 0), C.tot, y, { align: 'right' });
      doc.text(fmt(limpiezaRow(r)), C.limp, y, { align: 'right' });
      doc.text(fmt(comisionRow(r)), C.com, y, { align: 'right' });
      doc.text(fmt(r.renting || 0), C.rent, y, { align: 'right' });
      doc.setTextColor(...slate);
      doc.text(String(r.huespedes ?? '-'), C.n, y, { align: 'right' });
      y += 2.1;
      rule(M, y, RX, hairSoft, 0.4);
      y += 4;
    });

    // Totales
    if (ensure(7)) tableHeader();
    const tTot  = reservasDetalle.reduce((s, r) => s + (r.precioTotal || 0), 0);
    const tLimp = reservasDetalle.reduce((s, r) => s + limpiezaRow(r), 0);
    const tCom  = reservasDetalle.reduce((s, r) => s + comisionRow(r), 0);
    const tRent = reservasDetalle.reduce((s, r) => s + (r.renting || 0), 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.125);
    doc.setTextColor(...navy);
    doc.text('TOTALES', C.canal, y, { align: 'left' });
    doc.text(fmt(tTot),  C.tot,  y, { align: 'right' });
    doc.text(fmt(tLimp), C.limp, y, { align: 'right' });
    doc.text(fmt(tCom),  C.com,  y, { align: 'right' });
    doc.text(fmt(tRent), C.rent, y, { align: 'right' });
    y += 2.1;
    rule(M, y, RX, hair, 0.4);
    y += 5.6;
  }

  // ── Gastos adicionales ─────────────────────────────────────────────────────
  const gastos: any[] = (liq.conceptos || []).filter((c: any) => c.categoria === 'gasto');
  if (gastos.length > 0) {
    ensure(10 + gastos.length * 3.7);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...navy);
    doc.text(
      isLcm ? 'GASTOS ADICIONALES DEDUCIDOS (base imponible)' : 'GASTOS ADICIONALES DEDUCIDOS',
      M, y, { charSpace: 0.2 },
    );
    y += 3.7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.125);
    doc.setTextColor(...ink);
    gastos.forEach((g: any) => {
      ensure(6);
      const label = String(g.label || '')
        .replace(/\s*\(base imponible\)\s*$/i, '')
        .replace(/^\[(Mantenimiento|Lavandería)\]\s*/i, '');
      doc.text(clip(doc, label, 130), M, y);
      doc.text(fmtSigned(-Math.abs(g.pago || 0)), RX, y, { align: 'right' });
      y += 3.7;
    });

    const totalGastos = (isLcm ? (liq.gastosBI ?? liq.gastosTotal) : liq.gastosTotal) || 0;
    doc.setFont('helvetica', 'bold');
    doc.text('Total gastos', M, y);
    doc.text(fmtSigned(-Math.abs(totalGastos)), RX, y, { align: 'right' });
    y += 6.1;
  }

  // ── Nota de cálculo + pie ──────────────────────────────────────────────────
  const factor = fmtN((100 - owner.comision) / 100).replace(',00', '');
  const nota = isLcm
    ? `Cálculo (importes en base imponible): Base = Total / 1,1. Neto = Base - comisión - limpieza - renting - gastos. Base del alquiler = Neto × ${factor}.`
    : `Cálculo: Neto = Total - limpieza - comisión del canal - renting - gastos. Base del alquiler = Neto × ${factor}.`;

  ensure(12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.4);
  doc.setTextColor(...slate);
  const notaLines = doc.splitTextToSize(nota, RX - M) as string[];
  notaLines.forEach(l => { doc.text(l, M, y); y += 3.44; });
  doc.text(`Generado por ${SERENO.name} · ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, M, y);

  // Page numbers (only when the invoice spans more than one page)
  const pages = doc.getNumberOfPages();
  if (pages > 1) {
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.4);
      doc.setTextColor(...muted);
      doc.text(`Página ${p} de ${pages}`, RX, 288, { align: 'right' });
      doc.text(`${factNum} · ${owner.name}`, M, 288);
    }
  }

  return doc;
}

export function generateFacturaPDF(owner: PdfOwner, liq: any, periodo: string): void {
  const doc = _buildFacturaDoc(new jsPDF({ format: 'a4', unit: 'mm' }), owner, liq, periodo);
  doc.save(`Factura_${owner.name.replace(/\s+/g, '_')}_${periodo.replace(' ', '_')}.pdf`);
}

// ─── Main entry point ─────────────────────────────────────────────────────────

export function generateDoc(owner: PdfOwner, liq: any, reservas: any[], periodo: string): void {
  if (liq.tipo === 'liquidacion' || liq.tipo === 'rent2rent') {
    generateLiquidacionPDF(owner, liq, reservas, periodo);
  } else {
    generateFacturaPDF(owner, liq, periodo);
  }
}

/** Returns the PDF as a Blob (without triggering a download) */
export function generateDocBlob(owner: PdfOwner, liq: any, reservas: any[], periodo: string): Blob {
  if (liq.tipo === 'liquidacion' || liq.tipo === 'rent2rent') {
    return _buildLiquidacionDoc(new jsPDF({ format: 'a4', unit: 'mm' }), owner, liq, reservas, periodo).output('blob');
  } else {
    return _buildFacturaDoc(new jsPDF({ format: 'a4', unit: 'mm' }), owner, liq, periodo).output('blob');
  }
}
